import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useStyles from './styles';
import validate from 'validate.js';
import FormElements from './FormElements';
import { FORM_ELEMENT_TYPES } from './FormElements.types';
import Button from '@material-ui/core/Button';
import { checkFormHasError, getErrorFromErrorObj } from './utils';
import clsx from 'clsx';
import { removeSpacesFromString } from '../../utils/StringUtils';

const FormCreator = props => {
  const {
    validationSchema,
    editable,
    formElements,
    onSubmit,
    formValues,
    canSubmit,
    submitButtonTitle,
    alignHorizontal,
    onChange,
    resetTitle,
    resetHandler,
    canReset,
    skipErrorKeywords,
    setDataValidity,
    allowDisabledSubmit,
    transformValueBeforePrinting,
    className,
  } = props;

  const classes = useStyles();

  const [formState, setFormState] = useState(formValues);
  const [error, setError] = useState({});
  const [isValid, setIsValid] = useState(false);

  const handleChange = e => {
    const fieldName = (e.target.name || "").toLowerCase()
    if (fieldName.includes("phonenumber") || fieldName.includes("contactnumber") || fieldName.includes("mobilennumber")) {
      e.target.value = removeSpacesFromString(e.target.value)
    }
    if (onChange && typeof onChange === 'function') {
      onChange(e);
      return;
    }
    const newValue = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

    const finalObj = {...formState, [e.target.name]: newValue}
    if (e.target.extraField && e.target.extraValue) {
      finalObj[e.target.extraField] = e.target.extraValue
    }
    setFormState(finalObj);
  };

  const handleSubmit = () => {
    if (isValid) {
      onSubmit(formState);
    }
  };

  const handleSetDataValidity = (isValid) => {
    setIsValid(isValid);
    if (typeof setDataValidity === "function") {
      setDataValidity(isValid);
    }
  }

  useEffect(() => {
    setFormState(formValues);
  }, [formValues]);

  useEffect(() => {
    const errors = validate(formState, validationSchema) || {};
    const newErrors = {...errors};

    Object.keys(newErrors).forEach(key => {
      let errorMsgs = newErrors[key] || "";
      (errorMsgs || []).forEach((msg, index) => {
        let errorMsg = msg || "";
        (skipErrorKeywords || []).forEach(word => {
          errorMsg = errorMsg.replace(word, "");
        });
        errorMsgs[index] = (errorMsg || "").trim();
      });
      newErrors[key] = errorMsgs;
    });

    setError(newErrors);
    handleSetDataValidity(!checkFormHasError(errors));
  }, [formState, validationSchema]);

  return (
    <div className={clsx(classes.root, alignHorizontal && classes.alignHorizontal, className)}>
      {formElements.map((element, index) => {
        if (element.type === FORM_ELEMENT_TYPES.GROUP) {
          return (
            <div key={index} className={clsx(classes.elementGroup, `${element.name}Group`)}>
              <FormCreator
                alignHorizontal={true}
                canSubmit={false}
                editable={editable}
                formElements={element.values}
                formValues={formState}
                onChange={handleChange}
                validationSchema={validationSchema}
                transformValueBeforePrinting={transformValueBeforePrinting}
              />
            </div>
          );
        }

        const errorVal = getErrorFromErrorObj(error, element.name);

        return (
          <div key={element.name} className={classes.element}>
            <FormElements
              allowNone={element.allowNone}
              disabled={!editable}
              disabledForRoles={element.disabledForRoles}
              error={!!errorVal}
              helperText={errorVal ? errorVal : element.otherProps?.helperText}
              hiddenForRoles={element.hiddenForRoles}
              label={element.label}
              name={element.name}
              onChange={handleChange}
              otherProps={element.otherProps}
              type={element.type}
              value={formState[element.name]}
              childValue={formState[element.name + "_child"]}
              values={element.values}
              transformValueBeforePrinting={transformValueBeforePrinting}
            />
          </div>
        );
      })}
      {canSubmit ? (
        <div className={classes.submitButton}>
          {canReset ? (
            <Button
              className={classes.submitButton}
              color="secondary"
              onClick={resetHandler}
              size="large"
              variant="contained">
              {resetTitle}
            </Button>
          ) : null}
          <Button
            className={classes.submitButton}
            color="primary"
            disabled={!isValid || (!allowDisabledSubmit && !editable)}
            onClick={handleSubmit}
            size="large"
            variant="contained">
            {submitButtonTitle}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

FormCreator.propTypes = {
  validationSchema: PropTypes.object,
  formElements: PropTypes.array.isRequired,
  formValues: PropTypes.object,
  onSubmit: PropTypes.func,
  editable: PropTypes.bool,
  canSubmit: PropTypes.bool,
  canReset: PropTypes.bool,
  submitButtonTitle: PropTypes.string,
  alignHorizontal: PropTypes.bool,
  onChange: PropTypes.func,
  resetTitle: PropTypes.string,
  resetHandler: PropTypes.func,
  skipErrorKeywords: PropTypes.array,
  setDataValidity: PropTypes.func
};

FormCreator.defaultProps = {
  validationSchema: {},
  values: {},
  editable: true,
  canSubmit: true,
  canReset: false,
  onSubmit: () => null,
  submitButtonTitle: 'Submit',
  alignHorizontal: false,
  onChange: null,
  resetHandler: () => null,
  skipErrorKeywords: []
};

export default FormCreator;
